import { AwsRum, TargetDomEvent } from 'aws-rum-web';

import { EnvironmentConfig } from '../../Environments';

const DEFAULT_APPLICATION_VERSION = '1.0.0';
const DEFAULT_COOKIES_ENABLED = true;

/**
 * types of user interactions
 */
export type UserInteractions = 'click' | 'filter' | 'pagination' | 'select';

/**
 * a custom rum event
 */
export interface RumEvent {
    type: string;
    data: {
        user_interaction?: UserInteractions;
    };
}

/**
 * Replace spaces with dashes
 * table events use the table resourceName in the type
 * many contain spaces which are not allowed in RUM types
 * @param type 
 * @returns 
 */
const formatEventType = (type: string): string => {
    return type.replace(/\s/g, '-');
};

class RumClient {
    private rumClient: AwsRum;

    init = (env: EnvironmentConfig, allowCookies?: boolean, appVersion?: string) => {
        // Make sure we only ever have one instance
        if (this.rumClient !== undefined) return;

        // Rum config is optional per environment
        if (!env.rumConfig) {
            console.log('RUM disabled');
            return;
        }

        const { appId, identityPoolId, guestRoleArn, region } = env.rumConfig;

        try {
            this.rumClient = new AwsRum(
                appId,
                appVersion ?? DEFAULT_APPLICATION_VERSION,
                region,
                {
                    disableAutoPageView: true,
                    allowCookies: allowCookies ?? DEFAULT_COOKIES_ENABLED,
                    identityPoolId,
                    guestRoleArn,
                    endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
                    telemetries: ['performance', 'errors', 'http',
                        ['interaction', { 
                            /*
                             Setting enableMutationObserver to true allows web client to record events on all DOM elements,
                             including those added to the DOM after the window.load event was fired. 
                             When false, the web client will record events on only DOM elements that existed when the window.load event was fired.
                             https://github.com/aws-observability/aws-rum-web/blob/main/docs/configuration.md#interaction
                             */
                            'enableMutationObserver':true, 
                        } 
                        ]],
                }
            );
        } catch (error) {
            console.log('Failed to initialize RUM', error);
            return;
        }
    };

    enableCookies(enabled: boolean) {
        if (!this.rumClient) return;
        this.rumClient.allowCookies(enabled);
    };

    recordError(error: any) {
        if (!this.rumClient) return;
        this.rumClient.recordError(error);
    };

    recordPageView(pageId: string) {
        if (!this.rumClient) return;
        this.rumClient.recordPageView(pageId);
    };

    registerDomEvents(events: TargetDomEvent[]) {
        if (!this.rumClient) return;
        this.rumClient.registerDomEvents(events);
    };

    /**
     * Record a custom event in rum
     * https://docs.aws.amazon.com/AmazonCloudWatch/latest/monitoring/CloudWatch-RUM-custom-events.html
     * event type can be either the type or name of your event, must be between 1 and 256 characters
     * event type can be a combination of alphanumeric characters, underscores, hyphens, and periods
     * event data is of the form {field1: 1, field2: 2}
     * @param event 
     * @returns 
     */
    recordEvent(event: RumEvent) {
        if (!this.rumClient) {
            return;
        }
        this.rumClient.recordEvent(formatEventType(event.type), event.data);
    }
}

export default new RumClient();
